import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Center,
  Flex,
  HStack,
  Box,
  Text,
  Divider,
  Container,
  useTheme,
  Link,
  Image,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useDisclosure,
  AccordionItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  VStack,
} from '@chakra-ui/react';
import BackButton from '../../components/BackButton';
import {H3, TextG10, TextG12, TextG14} from '../../components/typography';
import useAuth from '../../hooks/useAuth';
import UserAvatar from '../../components/UserAvatar';
import ChatbotLauncher from '../../components/ChatbotLauncher';
import StarIconBlack from '../../assets/images/star-icon-black.png';
import ReferBackground from '../../assets/images/refer-background.jpg';
import EditProfile from './Edit';
import UserHandle from './Edit/UserHandle';
import LifestyleGoalsAndInterests from './LifestyleGoalsAndInterests';
import PointsAndRewards, {PointsAndRewardsTopBar} from './PointsAndRewards';
import {Wrapper} from '../../routes/AuthorizedRoutes';
import {BlueButton} from '../../components/button';
import {ToastMessage} from '../../components/toast';
import ExternalLink from '../../components/ExternalLink';
import {QRCodeSVG} from 'qrcode.react';
import {useBannerHeight} from '../../hooks/useTopBarOffset';

//const REFER_URL = 'www.walkabout.app/refer-a-friend?referrer=';

function Profile() {
  const navigate = useNavigate();
  const theme = useTheme();
  const {user, logout} = useAuth();
  const [drawerType, setDrawerType] = useState<string>();
  //const [drawerBody, setDrawerBody] = useState<JSX.Element>();

  const {isOpen, onOpen, onClose} = useDisclosure();
  const [margin, setMargin] = useState('0');
  const bannerHeight = useBannerHeight();

  const userReferLink = location.origin + '/refer-a-friend?invite-code=' + user?.referralCode;

  useEffect(() => {
    if (user && (user.lifestyleGroups.length < 1 || user.topics.length < 1)) {
      navigate('/profile/lifestyle-goals-and-interests');
    }
  }, [location.href]);

  const drawerBody = useMemo(() => {
    switch (drawerType) {
      case 'profileEdit':
        return <EditProfile onClose={onClose} />;
      case 'lifestyle':
        return <LifestyleGoalsAndInterests onClose={onClose} />;
      case 'points':
        return (
          <Wrapper
            onClose={onClose}
            Element={PointsAndRewards}
            CustomTopBar={PointsAndRewardsTopBar}
            progressBarProps={{hideHeader: true}}
            hideNavigationBar
          />
        );
      default:
        return null;
    }
  }, [drawerType]);

  const scrollToBottom = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight - window.innerHeight,
        behavior: 'smooth',
      });
    }, 300);
  };

  useEffect(() => {
    if (!isOpen) {
      setDrawerType('');
      setMargin('0');
    }
  }, [isOpen]);

  return (
    <Container pos="absolute" left={margin} top={`${bannerHeight}px`} style={{transition: 'all 0.33s ease-out'}}>
      <Flex flexDir={'column'} h="100vh" pb={4} justifyContent="space-between">
        <Box>
          <Box pt={6}>
            <Flex alignItems="center">
              <BackButton pos={'relative'} onClick={() => navigate('/home')} sticky={false} />
              <Box flex="1">
                <Center ml="-35px">
                  <H3>My Account</H3>
                </Center>
              </Box>
            </Flex>
          </Box>
          <Box mt="24px" py={5}>
            <Flex px="4px" alignItems="center">
              <UserAvatar user={user} size="lg" />
              <Flex mx="24px" flexDir="column" gap={1}>
                <TextG14 fontWeight="bold">{`${user?.name} ${user?.surname}`}</TextG14>
                <UserHandle text={user?.handle} />
              </Flex>
            </Flex>
          </Box>
          <Divider />
          <ChatbotLauncher
            customLauncher={
              <HStack spacing={4} px={2} py={5} w="100%">
                <i style={{marginTop: '6px'}} className="fi-rr-comment-user"></i>
                <TextG14>Help & Support</TextG14>
              </HStack>
            }
          />
          <Divider />
          <HStack
            px={2}
            spacing={4}
            py={5}
            w="100%"
            // onClick={() => navigate('/profile/points-and-rewards')}
            onClick={() => {
              setDrawerType('points');
              setMargin('-33%');
              onOpen();
            }}>
            <Image src={StarIconBlack} alt={'Star Icon'} w={'16px'} h={'16px'} />
            <TextG14>Points & Rewards</TextG14>
          </HStack>
          <Divider />
          <HStack
            spacing={4}
            px={2}
            py={5}
            w="100%"
            onClick={() => navigate('/my-stuff?tab=bookmarks')}>
            <i className="fi fi-rr-bookmark"></i>
            <TextG14>Bookmarks</TextG14>
          </HStack>
          <Divider />
          {/*<HStack spacing={4} px={2} py={5} w="100%">*/}
          {/*  <i className="fi fi-rr-chart-histogram"></i>*/}
          {/*  <TextG14>Activities</TextG14>*/}
          {/*</HStack>*/}
          {/*<Divider />*/}
          <HStack
            spacing={4}
            px={2}
            py={5}
            w="100%"
            onClick={() => {
              setDrawerType('lifestyle');
              setMargin('-33%');
              onOpen();
            }}
            // onClick={() => navigate('/profile/lifestyle-goals-and-interests')}
          >
            <i className="fi fi-rr-user"></i>
            <TextG14>Lifestyle Goals & Interests</TextG14>
          </HStack>
          <Divider />
          <HStack
            spacing={4}
            px={2}
            py={5}
            w="100%"
            onClick={() => {
              setDrawerType('profileEdit');
              setMargin('-33%');
              onOpen();
            }}
            //onClick={() => navigate(`/profile/edit/${user?.id}`)}
          >
            <i className="fi fi-rr-settings"></i>
            <TextG14>Account Settings</TextG14>
          </HStack>
          <Divider />
          <HStack
            spacing={4}
            px={2}
            py={5}
            w="100%"
            onClick={() => navigate('/membership-details')}>
            <i className="fi fi-rr-id-badge"></i>
            <TextG14>Membership Details</TextG14>
          </HStack>
          <Divider />
          <HStack
            spacing={4}
            px={2}
            py={5}
            w="100%"
            onClick={() => navigate('/blocked-users')}>
            <i className="fi fi-rr-ban"></i>
            <TextG14>Blocked Users</TextG14>
          </HStack>
          <Divider />
          {/* <HStack spacing={4} px={2} py={5} w="100%" onClick={() => navigate('/help')}>
        <i className="fi fi-rr-info"></i>
        <TextG14>Help</TextG14>
      </HStack> 
      <Divider /> */}
          <Divider />
          <HStack
            spacing={4}
            px={2}
            py={5}
            w="100%"
            onClick={() => navigate('/refer-a-friend')}>
            <i className="fi fi-rr-megaphone"></i>
            <TextG14>Refer a Friend</TextG14>
          </HStack>
          <Divider />
          <HStack spacing={4} px={2} py={5} w="100%" onClick={() => logout()}>
            <i className="fi fi-rr-power"></i>
            <TextG14>Logout</TextG14>
          </HStack>
        </Box>
        <VStack spacing={6} pb={6}>
          <Center
            textAlign="center"
            w="100%"
            pos="relative"
            left={margin}
            //bottom="7%"
            color={theme.colors.lighterBlue}>
            <TextG12>
              <ExternalLink
                _hover={{textDecoration: 'none'}}
                isExternal
                href={`${process.env.CONDITIONS_WEB_PAGE_URL}/privacy-policy/`}>
                Privacy
              </ExternalLink>
              {' & '}
              <ExternalLink
                _hover={{textDecoration: 'none'}}
                isExternal
                href={`${process.env.CONDITIONS_WEB_PAGE_URL}/terms-of-use/`}>
                Terms
              </ExternalLink>
            </TextG12>
          </Center>

        </VStack>
        <Drawer onClose={onClose} isOpen={isOpen} size="full">
          <DrawerContent
            motionProps={{
              variants: {
                enter: {
                  x: '0%',
                  transition: {duration: 0.2},
                },
                exit: {
                  x: '100%',
                  transition: {duration: 0.1},
                },
              },
            }}
            padding={0}
            boxShadow="dark-lg">
            <DrawerBody padding={0}>{drawerBody}</DrawerBody>
          </DrawerContent>
        </Drawer>
      </Flex>
    </Container>
  );
}

export default Profile;
